/**
 * Shared CSRF helpers for axios double-submit cookie posts (LINE WebView safe).
 */

export const resolveCsrfToken = (preferred = '') => {
	if (preferred) {
		return preferred;
	}
	try {
		const input = document.querySelector('input[name="_csrfToken"]');
		if (input?.value) {
			return input.value;
		}
		const meta = document.querySelector('meta[name="csrf-token"]');
		if (meta?.getAttribute('content')) {
			return meta.getAttribute('content');
		}
		const dataEl = document.querySelector('[data-csrf-token], [data-csrf_token]');
		if (dataEl) {
			return dataEl.getAttribute('data-csrf-token')
				|| dataEl.getAttribute('data-csrf_token')
				|| '';
		}
	} catch (_e) {}
	return '';
};

export const getCsrfAjaxHeaders = (csrfToken = '') => {
	const headers = {};
	const token = resolveCsrfToken(csrfToken);
	if (token) {
		headers['X-CSRF-Token'] = token;
	}
	return headers;
};

const _has_csrf_header = (headers) => {
	if (!headers) {
		return false;
	}
	return !!(headers['X-CSRF-Token'] || headers['x-csrf-token']);
};

const _attach_csrf_to_body = (data, token) => {
	if (!token || data == null) {
		return data;
	}
	if (typeof FormData !== 'undefined' && data instanceof FormData) {
		if (!data.has('_csrfToken')) {
			data.append('_csrfToken', token);
		}
		return data;
	}
	if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
		if (!data.has('_csrfToken')) {
			data.append('_csrfToken', token);
		}
		return data;
	}
	if (typeof data === 'object' && !Array.isArray(data) && typeof data !== 'string') {
		if (data._csrfToken == null || data._csrfToken === '') {
			return { ...data, _csrfToken: token };
		}
	}
	return data;
};

/**
 * Attach X-CSRF-Token (and _csrfToken body field when missing) on mutating axios calls.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 * @param {(() => string)|string} [getToken]
 */
export const installAxiosCsrfInterceptor = (axiosInstance, getToken = null) => {
	if (!axiosInstance?.interceptors?.request || axiosInstance.__xleadCsrfInterceptorInstalled) {
		return axiosInstance;
	}
	axiosInstance.interceptors.request.use((config) => {
		const method = String(config.method || 'get').toLowerCase();
		if (!['post', 'put', 'patch', 'delete'].includes(method)) {
			return config;
		}
		const preferred = typeof getToken === 'function' ? getToken() : (getToken || '');
		const token = resolveCsrfToken(preferred);
		if (!token) {
			return config;
		}
		config.headers = config.headers || {};
		if (!_has_csrf_header(config.headers)) {
			config.headers['X-CSRF-Token'] = token;
		}
		config.data = _attach_csrf_to_body(config.data, token);
		return config;
	});
	axiosInstance.__xleadCsrfInterceptorInstalled = true;
	return axiosInstance;
};
